import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

export type AppMode = 'personal' | 'business';

interface AppModeContextType {
	mode: AppMode;
	setMode: (mode: AppMode) => void;
}

const AppModeContext = createContext<AppModeContextType>({
	mode: 'personal', // Default mode
	setMode: () => {}, // Placeholder function
});

export const useAppMode = (): AppModeContextType => {
  const context = useContext(AppModeContext);
  return context;
};

let appMode: AppMode | undefined; // Global variable to store app mode
export function getAppMode(): AppMode {
	if (appMode) {
		return appMode;
	}

	// Check session storage for app mode
	// If not found, determine based on hostname
	const storedMode = sessionStorage.getItem('appMode') as AppMode;
	if (storedMode) {
		appMode = storedMode;
	} else {
		const hostname = window.location.hostname.replace(/^www\./, '');
		appMode = hostname.startsWith('business.') ? 'business' : 'personal';
	}
	
	return appMode;
};

export function getUrlForAppMode(mode: AppMode): string {
	const hostname = window.location.hostname.replace(/^www\./, '');
	const isLocalhost = hostname === 'localhost';
	const isIpAddress = /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname);

	// If the app is running on localhost or an IP address, return the current URL
	if (isLocalhost || isIpAddress) {
		return window.location.href;
	}

	const pageUrl = new URL(window.location.href);
	if (mode === 'business' && !hostname.startsWith('business.')) {
		pageUrl.hostname = `business.${hostname}`;
	} else if (hostname.startsWith('business.')) {
		pageUrl.hostname = hostname.replace('business.', '');
	}

	return pageUrl.href;
};

export function AppModeProvider({ children }: { children: React.ReactNode }) {
	const navigate = useNavigate();
	const [mode, setModeState] = useState<AppMode>(getAppMode());

	// Initialize mode based on session storage or URL hostname
	useEffect(() => {
		const appMode = getAppMode();
		console.log("App mode initialized:", appMode);
		setModeState(appMode);
	}, []);

	const setMode = useCallback((newMode: AppMode) => {
		if (newMode === appMode) return; // No change needed

		const hostname = window.location.hostname.replace(/^www\./, '');
		const isLocalhost = hostname === 'localhost';
		const isIpAddress = /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname);

		if (isLocalhost || isIpAddress) {
			// Switch mode by updating session storage
			sessionStorage.setItem('appMode', newMode);
			setModeState(newMode);
			navigate(0); // Force reload to apply changes
			return;
		}

		setModeState(newMode);
		const newUrl = getUrlForAppMode(newMode);
		navigate(newUrl);
		
	}, [navigate]);

	return (
    <AppModeContext.Provider value={{ mode, setMode }}>
      {children}
    </AppModeContext.Provider>
  );
};
